import React, { useState, useEffect, useRef } from "react";
import { AirQualityService, SensorDataResponse } from "../../services/airQualityService";
import { regions, getRegionByName } from "../../constants/regions";
import { getLocationsApi, getLocationDetailApi, LocationDetailResponseDto, LocationDeviceDto } from "../../lib/api/location";
import { getAllDataCriteriaApi } from "../../lib/api/dataCriteria";
import { DataCriteriaGroupResponseType, CriteriaType, Grade, GradeKoreanName } from "../../types/dataCriteria";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";

// 타입 정의
interface MarkerData {
  id: string;
  locationId: number;
  name: string;
  position: { lat: number; lng: number };
  type: "good" | "normal" | "warning" | "danger";
  deviceCount: number;
  devices: LocationDeviceDto[];
  data: {
    nh3: number;
    h2s: number;
    ch3sh: number;
    tvoc: number;
    temperature: number;
    humidity: number;
    windDirection: string;
    windSpeed: number;
  };
  latestSensorData?: SensorDataResponse[];
}

// 사업장 데이터를 마커 데이터로 변환하는 함수
const createMarkerFromLocation = (location: LocationDetailResponseDto): MarkerData => {
  return {
    id: location.id.toString(),
    locationId: location.id,
    name: location.locationName,
    position: { 
      lat: location.latitude || 35.0156, 
      lng: location.longitude || 126.7108 
    },
    type: "good", // 초기값, 나중에 센서 데이터로 업데이트
    deviceCount: location.currentDeviceCount || 0,
    devices: location.devices || [],
    data: {
      nh3: 0,
      h2s: 0,
      ch3sh: 0,
      tvoc: 0,
      temperature: 0,
      humidity: 0,
      windDirection: "북동",
      windSpeed: 0,
    },
    latestSensorData: [],
  };
};

// CustomTooltip 컴포넌트에 타입 추가
interface CustomTooltipProps {
  active?: boolean;
  payload?: any[];
  label?: string;
}

// 차트 커스텀 툴팁
const CustomTooltip: React.FC<CustomTooltipProps> = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    // 현재 날짜 정보 (MM.dd 형식)
    const today = new Date();
    const dateStr = `${(today.getMonth() + 1).toString().padStart(2, '0')}.${today.getDate().toString().padStart(2, '0')}`;
    
    // 시간 정보는 payload.time에서 가져옴
    const timeStr = payload[0].payload?.time || label;
    
    return (
        <div className="chart-tooltip">
          <p style={{ margin: 0, whiteSpace: 'pre-line', color: 'white' }}>
            {`${dateStr}/${timeStr}\n값: ${payload[0].value.toFixed(2)}`}
          </p>
        </div>
    );
  }
  return null;
};

const MonitoringView = () => {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<any>(null);
  const markersRef = useRef<any[]>([]);
  const infoWindowRef = useRef<any>(null);

  const [selectedMarker, setSelectedMarker] = useState<MarkerData | null>(null);
  const [selectedSensor, setSelectedSensor] = useState<string>("종합");
  const [currentTime, setCurrentTime] = useState(new Date());
  const [dataCriteria, setDataCriteria] = useState<DataCriteriaGroupResponseType[]>([]);
  const [deviceData, setDeviceData] = useState<MarkerData[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedRegion, setSelectedRegion] = useState<string>("");
  const [searchKeyword, setSearchKeyword] = useState<string>("");
  const [showGraphPopup, setShowGraphPopup] = useState(false);
  const [chartData, setChartData] = useState([]);
  const [selectedMetric, setSelectedMetric] = useState("복합악취");
  const [popupDeviceData, setPopupDeviceData] = useState<MarkerData | null>(null);
  const [popupSensorData, setPopupSensorData] = useState<SensorDataResponse[]>([]);
  const [chartLoading, setChartLoading] = useState(false);

  const airQualityService = AirQualityService.getInstance();

  // 시간 업데이트
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  // 데이터 기준 로드
  useEffect(() => {
    const loadDataCriteria = async () => {
      try {
        const criteria = await getAllDataCriteriaApi();
        setDataCriteria(criteria);
        console.log("Data criteria loaded:", criteria);
      } catch (error) {
        console.error("Failed to load data criteria:", error);
      }
    };
    loadDataCriteria();
  }, []);

  // 실제 데이터 로드
  const loadLocationData = async () => {
    try {
      setLoading(true);
      console.log("Loading location data...");
      
      // 사업장 목록 조회 (모든 사업장 포함)
      const locationResponse = await getLocationsApi({
        size: 100, // 충분히 큰 사이즈로 모든 사업장 조회
        page: 0
      });
      console.log("Location response:", locationResponse);
      console.log(`Total locations found: ${locationResponse.content.length}`);
      
      const updatedData: MarkerData[] = [];

      for (const location of locationResponse.content) {
        try {
          console.log(`Processing location: ${location.locationName} (ID: ${location.id})`);
          
          // 사업장 상세 정보 가져오기 (기기 정보 포함)
          let locationDetail: LocationDetailResponseDto;
          try {
            locationDetail = await getLocationDetailApi(location.id);
            console.log(`Location detail for ${location.locationName}:`, locationDetail);
          } catch (error) {
            console.warn(`Failed to get detail for location ${location.locationName}, using basic info:`, error);
            locationDetail = location as LocationDetailResponseDto;
          }
          
          // 사업장을 마커 데이터로 변환
          const markerData = createMarkerFromLocation(locationDetail);
          console.log(`Marker data created for ${location.locationName}:`, markerData);
          
          // 사업장의 기기들에 대한 센서 데이터 수집
          console.log(`Device check for ${locationDetail.locationName}: deviceCount=${locationDetail.currentDeviceCount}, devices.length=${markerData.devices.length}`);
          
          // 활성화된 기기가 있는지 확인
          const activeDevices = markerData.devices.filter(device => device.status === 'ACTIVE');
          console.log(`Active devices for ${locationDetail.locationName}:`, activeDevices.length);
          console.log(`Device details for ${locationDetail.locationName}:`, markerData.devices);
          
          if (locationDetail.currentDeviceCount > 0 && activeDevices.length > 0) {
            console.log(`Loading sensor data for location ${locationDetail.locationName}...`);
            
            // 모든 기기의 센서 데이터를 종합
            let totalNh3 = 0, totalH2s = 0, totalCh4 = 0, totalTemp = 0, totalHumidity = 0;
            let dataCount = 0;
            const allSensorData: SensorDataResponse[] = [];

            // 각 활성화된 기기의 데이터를 가져와서 평균 계산
            for (const device of activeDevices) {
              try {
                // device.device.id를 사용하여 API 호출 (내부 ID 사용)
                const internalDeviceId = device.device?.id || parseInt(device.deviceCode);
                
                console.log(`Trying to load sensor data for device:`, device, `internalDeviceId: ${internalDeviceId}`);
                
                // 모니터링용 최신 데이터 1개만 조회 (성능 최적화)
                const latestData = await airQualityService.getDeviceLatestData(internalDeviceId);
                
                if (latestData) {
                  // 메타 정보 추가하여 센서 데이터 저장
                  allSensorData.push({
                    ...latestData,
                    meta: {
                      ...latestData.meta,
                      deviceId: internalDeviceId.toString(),
                      sensorNumber: device.sensorNumber?.toString()
                    }
                  });
                  
                  totalNh3 += latestData.nh3 || 0;
                  totalH2s += latestData.h2s || 0;
                  totalCh4 += latestData.ch4 || 0;
                  totalTemp += latestData.temperature || 0;
                  totalHumidity += latestData.humidity || 0;
                  dataCount++;
                }
              } catch (error) {
                console.error(`Failed to load data for device ${device.deviceId}:`, error);
                // 에러 발생 시 해당 기기는 에러 상태로 처리
                allSensorData.push({
                  time: new Date().toISOString(),
                  meta: { deviceId: device.deviceId.toString() },
                  tv: -1,
                  hv: -1,
                  ws: -1,
                  wd: -1,
                  h2s: -1, // -1로 에러 표시
                  nh3: -1,
                  ch4: -1,
                  temperature: -1,
                  humidity: -1,
                  measuredAt: new Date().toISOString(),
                });
              }
            }

            // 평균값 계산 및 에러 처리
            const hasErrorData = allSensorData.some(data => data.h2s === -1 || data.nh3 === -1);
            
            if (hasErrorData) {
              // 에러 데이터가 있으면 모든 값을 -1로 설정 (UI에서 "-"로 표시됨)
              markerData.data = {
                nh3: -1,
                h2s: -1,
                ch3sh: -1,
                tvoc: -1,
                temperature: -1,
                humidity: -1,
                windDirection: "-",
                windSpeed: -1,
              };
              markerData.latestSensorData = allSensorData;
              markerData.type = "good"; // 에러 시에도 좋음으로 표시
            } else if (dataCount > 0) {
              markerData.data = {
                nh3: totalNh3 / dataCount,
                h2s: totalH2s / dataCount,
                ch3sh: totalNh3 / dataCount, // ch3sh는 실제로는 nh3 데이터를 사용 (UI 표시용)
                tvoc: totalCh4 / dataCount,
                temperature: totalTemp / dataCount,
                humidity: totalHumidity / dataCount,
                windDirection: "북동",
                windSpeed: 0,
              };
              markerData.latestSensorData = allSensorData;
              markerData.type = getLocationStatus(markerData.data);
            } else {
              // 활성화된 기기가 있지만 센서 데이터가 없을 때는 에러 데이터로 처리
              if (activeDevices.length > 0) {
                console.log(`No sensor data found for location ${locationDetail.locationName}, showing as error data`);
                markerData.data = {
                  nh3: -1,
                  h2s: -1,
                  ch3sh: -1,
                  tvoc: -1,
                  temperature: -1,
                  humidity: -1,
                  windDirection: "-",
                  windSpeed: -1,
                };
                markerData.type = "good"; // 에러 시에도 좋음으로 표시
                console.log(`Error data applied for ${locationDetail.locationName}:`, markerData.data);
              } else {
                // 활성화된 기기가 없으면 모든 값을 "-"로 표시
                console.log(`No active devices for ${locationDetail.locationName}, showing as no data (-)`);
                markerData.data = {
                  nh3: -1,
                  h2s: -1,
                  ch3sh: -1,
                  tvoc: -1,
                  temperature: -1,
                  humidity: -1,
                  windDirection: "-",
                  windSpeed: -1,
                };
                markerData.type = "good";
              }
            }
          } else {
            // 기기가 없는 사업장은 모든 값을 "-"로 표시
            console.log(`No devices in location ${locationDetail.locationName}, showing as no data (-)`);
            markerData.data = {
              nh3: -1,
              h2s: -1,
              ch3sh: -1,
              tvoc: -1,
              temperature: -1,
              humidity: -1,
              windDirection: "-",
              windSpeed: -1,
            };
            markerData.type = "good"; // 기기가 없으면 좋음으로 설정
            console.log(`No data applied for ${locationDetail.locationName}:`, markerData.data);
          }
          
          updatedData.push(markerData);
          console.log(`Successfully processed location: ${locationDetail.locationName || location.locationName}`);
        } catch (error) {
          console.error(`Failed to process location ${location.locationName}:`, error);
        }
      }

      console.log(`Total markers created: ${updatedData.length}`);
      setDeviceData(updatedData);
    } catch (error) {
      console.error("Failed to load location data:", error);
    } finally {
      setLoading(false);
    }
  };

  // 기기 상태 계산 함수 - 백엔드 등급 기준 사용
  const getDeviceStatus = (sensorData: SensorDataResponse): "good" | "normal" | "warning" | "danger" => {
    // 백엔드에서 이미 계산된 compositeGrade 사용
    if (sensorData.compositeGrade) {
      return convertGradeToStatus(sensorData.compositeGrade);
    }
    return "good"; // 기본값
  };

  // 사업장 상태 계산 함수 - 백엔드와 동일한 로직 사용
  const getLocationStatus = (data: any): "good" | "normal" | "warning" | "danger" => {
    // 평균 데이터에서 최고 등급 기준으로 판정
    if (data.h2s === -1 || data.nh3 === -1) return "good"; // 에러 데이터는 좋음으로 표시
    
    // 실제 백엔드 등급 기준 대신 임시로 대시보드와 유사한 로직 사용
    // TODO: 향후 백엔드 API에서 직접 등급을 받아오도록 개선
    const h2s = data.h2s || 0;
    const nh3 = data.nh3 || 0;
    
    // 대시보드와 동일한 기준 적용 (백엔드 DataCriteria 기준 근사치)
    if (h2s <= 5 && nh3 <= 50) return "good";
    if (h2s <= 20 && nh3 <= 200) return "normal";  
    if (h2s <= 100 && nh3 <= 1000) return "warning";
    return "danger";
  };

  // 한글 등급을 상태로 변환하는 헬퍼 함수
  const convertGradeToStatus = (grade: string): "good" | "normal" | "warning" | "danger" => {
    switch (grade) {
      case "좋음": return "good";
      case "보통": return "normal";
      case "나쁨": return "warning";
      case "매우나쁨": return "danger";
      default: return "good";
    }
  };

  // 데이터 로드 useEffect
  useEffect(() => {
    loadLocationData();
    
    // 5분마다 데이터 갱신
    const dataTimer = setInterval(loadLocationData, 5 * 60 * 1000);
    
    return () => clearInterval(dataTimer);
  }, []);

  // 지역 검색 기능
  const handleRegionChange = (regionName: string) => {
    setSelectedRegion(regionName);
    if (mapInstanceRef.current) {
      if (regionName) {
        // 특정 지역 선택 시
        const regionData = getRegionByName(regionName);
        if (regionData) {
          // 지도 중심을 해당 지역으로 이동
          const moveLatLng = new window.kakao.maps.LatLng(regionData.lat, regionData.lng);
          mapInstanceRef.current.setCenter(moveLatLng);
          mapInstanceRef.current.setLevel(5); // 줌 레벨 조정
        }
      } else {
        // "전체" 선택 시 기본 위치로 리셋
        const defaultLatLng = new window.kakao.maps.LatLng(35.0156, 126.7108);
        mapInstanceRef.current.setCenter(defaultLatLng);
        mapInstanceRef.current.setLevel(7); // 기본 줌 레벨로 리셋
      }
    }
  };

  // 검색어로 지역 검색
  const handleSearch = () => {
    if (searchKeyword.trim() && mapInstanceRef.current) {
      const regionData = getRegionByName(searchKeyword.trim());
      if (regionData) {
        const moveLatLng = new window.kakao.maps.LatLng(regionData.lat, regionData.lng);
        mapInstanceRef.current.setCenter(moveLatLng);
        mapInstanceRef.current.setLevel(5);
        setSelectedRegion(regionData.name);
      } else {
        alert(`"${searchKeyword}"에 해당하는 지역을 찾을 수 없습니다.`);
      }
    }
  };

  // 엔터키 검색
  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  // 그래프 팝업 열기
  const openGraphPopup = async (location: MarkerData) => {
    setPopupDeviceData(location);
    setShowGraphPopup(true);
    setChartLoading(true);
    
    await loadChartDataForSensor(location, selectedSensor);
  };

  // 선택된 센서에 대한 차트 데이터 로드
  const loadChartDataForSensor = async (location: MarkerData, sensor: string) => {
    try {
      // 선택된 센서에 따라 기기 데이터 로드
      if (location.devices && location.devices.length > 0) {
        let targetDevice;
        
        // 센서 선택에 따른 기기 결정
        if (sensor === "종합") {
          // 종합은 센서1 데이터 사용
          targetDevice = location.devices.find(d => d.sensorNumber === 1) || location.devices[0];
        } else {
          // 센서1-5 선택시 해당 센서 번호의 기기 사용
          const sensorNumber = parseInt(sensor.replace("센서", ""));
          targetDevice = location.devices.find(d => d.sensorNumber === sensorNumber);
        }
        
        if (targetDevice) {
          const internalDeviceId = targetDevice.device?.id || parseInt(targetDevice.deviceCode);
          
          console.log(`Loading chart data for sensor ${sensor}, deviceId: ${internalDeviceId}`);
          
          const data = await airQualityService.getDeviceTodayDataForMonitoring(internalDeviceId);
          setPopupSensorData(data);
          generateChartDataFromReal(data);
          setChartLoading(false);
        } else {
          // 선택된 센서가 없는 경우
          console.log(`No device found for sensor ${sensor}`);
          setPopupSensorData([]);
          generateErrorChartData();
          setChartLoading(false);
        }
      } else {
        generateErrorChartData(); // 에러 차트 데이터 생성
        setChartLoading(false);
      }
    } catch (error) {
      console.error("Failed to load sensor data:", error);
      setPopupSensorData([]);
      generateErrorChartData(); // fallback to error data
      setChartLoading(false);
    }
  };

  // 실제 데이터로부터 차트 데이터 생성 (30분 단위로 그룹화)
  const generateChartDataFromReal = (data: SensorDataResponse[]) => {
    if (!data || data.length === 0) {
      generateErrorChartData();
      return;
    }

    // 30분 단위로 데이터 그룹화
    const groupedData = new Map();
    
    data.forEach((item) => {
      const time = new Date(item.measuredAt);
      // 30분 단위로 반올림 (0분, 30분)
      const minutes = time.getMinutes() >= 30 ? 30 : 0;
      const roundedTime = new Date(time.getFullYear(), time.getMonth(), time.getDate(), time.getHours(), minutes);
      const timeKey = roundedTime.getTime();
      
      let value;
      switch (selectedMetric) {
        case "복합악취":
          // 복합악취는 외부 API에서 추후 제공 예정으로 현재는 0으로 표기
          value = 0;
          break;
        case "황화수소":
          value = item.h2s || 0;
          break;
        case "암모니아":
          value = item.nh3 || 0;
          break;
        case "메탄가스":
          value = item.ch4 || 0;
          break;
        case "온도":
          value = item.tv || item.temperature || 0; // tv 필드 우선 사용
          break;
        case "습도":
          value = item.hv || item.humidity || 0; // hv 필드 우선 사용
          break;
        default:
          value = 0;
      }

      if (!groupedData.has(timeKey)) {
        groupedData.set(timeKey, {
          time: roundedTime,
          values: [],
          originalTime: item.measuredAt
        });
      }
      groupedData.get(timeKey).values.push(value);
    });

    // 평균값 계산 및 정렬
    const chartData = Array.from(groupedData.values())
      .map(group => {
        const avgValue = group.values.reduce((sum, val) => sum + val, 0) / group.values.length;
        const timeStr = `${group.time.getHours().toString().padStart(2, "0")}:${group.time.getMinutes().toString().padStart(2, "0")}`;
        
        return {
          time: timeStr,
          value: Number(avgValue.toFixed(3)),
          originalTime: group.originalTime // 툴팁용 원본 시간
        };
      })
      .sort((a, b) => a.time.localeCompare(b.time));

    setChartData(chartData);
  };

  // 에러 차트 데이터 생성 (30분 단위로 0값 설정)
  const generateErrorChartData = () => {
    const data = [];
    for (let i = 0; i < 24; i++) {
      const hour = i.toString().padStart(2, "0");
      // 매 시간마다 00분, 30분 두 개의 데이터 포인트 생성
      data.push({
        time: `${hour}:00`,
        value: 0,
      });
      data.push({
        time: `${hour}:30`,
        value: 0,
      });
    }
    setChartData(data);
  };

  // 목 데이터 생성
  const generateMockChartData = () => {
    const data = [];
    for (let i = 0; i < 24; i++) {
      const hour = i.toString().padStart(2, "0");
      let value;

      switch (selectedMetric) {
        case "복합악취":
          value = 0.2 + Math.random() * 1.3;
          break;
        case "황화수소":
          value = 7.5 + Math.random() * 2;
          break;
        case "암모니아":
          value = 0.5 + Math.random() * 1.5;
          break;
        case "메탄가스":
          value = 100 + Math.random() * 60;
          break;
        case "온도":
          value = 25 + Math.random() * 5;
          break;
        case "습도":
          value = 40 + Math.random() * 30;
          break;
        default:
          value = 0;
      }

      data.push({
        time: `${hour}:00`,
        value: Number(value.toFixed(2)),
      });
    }
    setChartData(data);
  };

  // 센서 변경시 새로운 데이터 로드 (API 호출 필요)
  useEffect(() => {
    if (showGraphPopup && popupDeviceData) {
      setChartLoading(true);
      // 에러 상태인 경우 에러 데이터로 다시 생성
      if (popupDeviceData.data.nh3 === -1) {
        generateErrorChartData();
        setChartLoading(false);
      } else {
        loadChartDataForSensor(popupDeviceData, selectedSensor);
      }
    }
  }, [selectedSensor]);

  // 메트릭 변경시 기존 데이터로 차트 재생성 (API 호출 없음)
  useEffect(() => {
    if (showGraphPopup && popupSensorData.length > 0) {
      generateChartDataFromReal(popupSensorData);
    }
  }, [selectedMetric]);

  // 데이터가 로드되면 마커 생성
  useEffect(() => {
    if (deviceData.length > 0 && mapInstanceRef.current) {
      createMarkers();
    }
  }, [deviceData]);

  // 카카오맵 스크립트 로드
  useEffect(() => {
    // 이미 로드된 경우 처리
    if (window.kakao && window.kakao.maps) {
      const checkAndInitMap = () => {
        if (window.kakao.maps.LatLng) {
          initializeMap();
        } else if (window.kakao.maps.load) {
          window.kakao.maps.load(() => {
            initializeMap();
          });
        } else {
          setTimeout(checkAndInitMap, 100);
        }
      };
      checkAndInitMap();
      return;
    }

    // 스크립트 동적 로드
    const script = document.createElement("script");
    script.src = `//dapi.kakao.com/v2/maps/sdk.js?appkey=${import.meta.env.VITE_KAKAO_MAP_KEY}&autoload=false`;
    script.async = true;
    document.head.appendChild(script);

    script.onload = () => {
      window.kakao.maps.load(() => {
        initializeMap();
      });
    };

    return () => {
      if (markersRef.current) {
        markersRef.current.forEach((marker) => marker.setMap(null));
      }
      if (infoWindowRef.current) {
        infoWindowRef.current.close();
      }
    };
  }, []);

  const initializeMap = () => {
    const container = mapRef.current;
    if (!container || !window.kakao || !window.kakao.maps) return;

    try {
      const options = {
        center: new window.kakao.maps.LatLng(35.0156, 126.7108),
        level: 7,
        draggable: true,
        scrollwheel: true,
        disableDoubleClick: false,
        keyboardShortcuts: true,
      };

      const map = new window.kakao.maps.Map(container, options);
      mapInstanceRef.current = map;

      // 지도 타입을 위성지도로 설정
      //map.setMapTypeId(window.kakao.maps.MapTypeId.HYBRID);

      // 지도 타입 컨트롤 추가
      const mapTypeControl = new window.kakao.maps.MapTypeControl();
      map.addControl(mapTypeControl, window.kakao.maps.ControlPosition.TOPRIGHT);

      // 줌 컨트롤 추가
      const zoomControl = new window.kakao.maps.ZoomControl();
      map.addControl(zoomControl, window.kakao.maps.ControlPosition.RIGHT);

      // 마커 생성은 데이터 로드 후에 수행
    } catch (error) {
      console.error("카카오맵 초기화 실패:", error);
    }
  };

  const createMarkers = () => {
    if (!mapInstanceRef.current) return;

    // 기존 마커 제거
    markersRef.current.forEach((marker) => marker.setMap(null));
    markersRef.current = [];

    deviceData.forEach((location) => {
      // 마커 이미지 설정
      const imageSrc = getMarkerImage(location.type);
      const imageSize = new window.kakao.maps.Size(50, 50);
      const imageOption = { offset: new window.kakao.maps.Point(25, 25) };
      const markerImage = new window.kakao.maps.MarkerImage(imageSrc, imageSize, imageOption);

      const marker = new window.kakao.maps.Marker({
        position: new window.kakao.maps.LatLng(location.position.lat, location.position.lng),
        map: mapInstanceRef.current,
        image: markerImage,
      });

      // 마커 클릭 이벤트
      window.kakao.maps.event.addListener(marker, "click", () => {
        showInfoWindow(location, marker);
      });

      markersRef.current.push(marker);
    });
  };

  const getMarkerImage = (type: string) => {
    const images = {
      good: "/img/ds_ico01.png",
      normal: "/img/ds_ico02.png",
      warning: "/img/ds_ico03.png",
      danger: "/img/ds_ico04.png",
    };

    return images[type as keyof typeof images] || "/img/ds_ico02.png";
  };

  const showInfoWindow = (location: MarkerData, marker: any) => {
    setSelectedMarker(location);

    if (infoWindowRef.current) {
      infoWindowRef.current.close();
    }

    // 초기 센서는 종합으로 설정
    const currentSensor = "종합";
    const content = createInfoWindowContent(location, currentSensor);

    const infoWindow = new window.kakao.maps.InfoWindow({
      content: content,
      removable: true,
    });

    infoWindow.open(mapInstanceRef.current, marker);
    infoWindowRef.current = infoWindow;

    // DOM이 생성된 후 이벤트 바인딩
    setTimeout(() => {
      attachInfoWindowEvents(location, currentSensor);
    }, 100);
  };

  const createInfoWindowContent = (location: MarkerData, currentSensor: string = "종합") => {
    // 선택된 센서에 따라 데이터 결정
    let currentData = location.data;
    
    if (currentSensor !== "종합" && location.latestSensorData) {
      // 특정 센서가 선택된 경우 해당 센서의 데이터 찾기
      const sensorNumber = parseInt(currentSensor.replace("센서", ""));
      const targetDevice = location.devices?.find(d => d.sensorNumber === sensorNumber);
      
      if (targetDevice && location.latestSensorData) {
        // 해당 기기의 센서 데이터 찾기
        const deviceData = location.latestSensorData.find(data => 
          data.meta?.deviceId === targetDevice.deviceId.toString() ||
          data.meta?.deviceId === targetDevice.device?.id?.toString()
        );
        
        if (deviceData) {
          currentData = {
            nh3: deviceData.nh3 || 0,
            h2s: deviceData.h2s || 0,
            ch3sh: deviceData.nh3 || 0, // ch3sh는 실제로는 nh3 데이터 사용
            tvoc: deviceData.ch4 || 0,
            temperature: deviceData.temperature || 0,
            humidity: deviceData.humidity || 0,
            windDirection: deviceData.wd ? `${deviceData.wd}°` : "-",
            windSpeed: deviceData.ws || 0,
          };
        }
      }
    }

    const formatValue = (value: number) => {
      return value === -1 ? "-" : value.toFixed(2);
    };

    const getStatus = (value: number, type: string) => {
      if (value === -1) return "데이터없음";
      
      // DataCriteria 기준 사용 (SINGLE_DEVICE 타입)
      const singleDeviceCriteria = dataCriteria.find(
        criteria => criteria.criteriaType === CriteriaType.SINGLE_DEVICE
      );
      
      if (singleDeviceCriteria && singleDeviceCriteria.grades) {
        // 등급을 좋음->보통->나쁨->매우나쁨 순으로 정렬
        const sortedGrades = [...singleDeviceCriteria.grades].sort((a, b) => {
          const gradeOrder = { 'GOOD': 0, 'NORMAL': 1, 'BAD': 2, 'VERY_BAD': 3 };
          return gradeOrder[a.grade] - gradeOrder[b.grade];
        });
        
        for (const criteria of sortedGrades) {
          let thresholdValue: number;
          
          // 가스 타입에 따른 임계값 선택
          if (type === "nh3") {
            thresholdValue = criteria.nh3Value;
          } else if (type === "h2s") {
            thresholdValue = criteria.h2sValue;
          } else if (type === "tvoc") {
            thresholdValue = criteria.ch4Value;
          } else {
            // 복합악취의 경우 종합 기준 사용
            continue;
          }
          
          // "이하" 기준으로 비교
          if (criteria.comparisonType === "이하" && value <= thresholdValue) {
            return criteria.gradeKoreanName;
          }
        }
        
        // 모든 기준을 초과하면 매우나쁨
        return "매우나쁨";
      }
      
      // DataCriteria를 불러오지 못한 경우 기존 로직 사용 (fallback)
      if (type === "nh3") {
        if (value <= 50) return "좋음";
        if (value <= 200) return "보통";
        if (value <= 1000) return "나쁨";
        return "매우나쁨";
      } else if (type === "h2s") {
        if (value <= 5) return "좋음";
        if (value <= 20) return "보통";
        if (value <= 100) return "나쁨";
        return "매우나쁨";
      } else if (type === "tvoc") {
        if (value <= 5) return "좋음";
        if (value <= 20) return "보통";
        if (value <= 100) return "나쁨";
        return "매우나쁨";
      }
      
      return "좋음";
    };

    // 복합악취(종합) 등급 계산 함수
    const getCompositeStatus = (h2sValue: number, nh3Value: number) => {
      if (h2sValue === -1 && nh3Value === -1) return "데이터없음";
      
      // 개별 등급 계산
      const h2sGrade = getStatus(h2sValue, "h2s");
      const nh3Grade = getStatus(nh3Value, "nh3");
      
      // 종합 등급 로직: 더 나쁜 등급 우선 적용
      // 1. 둘 중 하나라도 "매우나쁨"이면 → "매우나쁨"
      if (h2sGrade === "매우나쁨" || nh3Grade === "매우나쁨") {
        return "매우나쁨";
      }
      
      // 2. 둘 중 하나라도 "나쁨"이면 → "나쁨"  
      if (h2sGrade === "나쁨" || nh3Grade === "나쁨") {
        return "나쁨";
      }
      
      // 3. 둘 중 하나라도 "보통"이면 → "보통"
      if (h2sGrade === "보통" || nh3Grade === "보통") {
        return "보통";
      }
      
      // 4. 둘 다 "좋음"이면 → "좋음"
      if (h2sGrade === "좋음" && nh3Grade === "좋음") {
        return "좋음";
      }
      
      // 5. 데이터 없는 경우
      if (h2sGrade === "데이터없음" && nh3Grade === "데이터없음") {
        return "데이터없음";
      }
      
      // 6. 하나만 데이터가 있는 경우, 해당 데이터의 등급 사용
      if (h2sGrade === "데이터없음") return nh3Grade;
      if (nh3Grade === "데이터없음") return h2sGrade;
      
      return "좋음"; // fallback
    };

    const getStatusClass = (status: string) => {
      switch (status) {
        case "좋음":
          return "good";
        case "보통":
          return "normal";
        case "나쁨":
          return "bad";
        case "매우나쁨":
          return "very-bad";
        case "데이터없음":
          return "no-data";
        default:
          return "";
      }
    };

    return `
      <div class="info-window" style="
        background: white;
        box-shadow: 0 2px 8px rgba(0,0,0,0.15);
        width: 475px;
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      ">
        <div class="info-header" style="
          background-color: #24aca7;
          padding: 12px 15px;
          color: white;
        ">
          <div class="weather-section" style="display: flex; justify-content: space-between; align-items: center;">
            <h3 style="margin: 0; font-size: 18px; font-weight: 700; color:#ffffff; ">${location.name} (기기수량${location.deviceCount})</h3>
            <span style="font-size: 18px; font-weight: 700; margin-right:20px; color:#ffffff;">${currentTime.toLocaleString(
              "ko-KR",
              {
                year: "numeric",
                month: "2-digit",
                day: "2-digit",
                hour: "2-digit",
                minute: "2-digit",
                second: "2-digit",
              }
            )}</span>
          </div>
        </div>
        
        <div class="sensor-tabs" style="
          margin-bottom: 0px;
          display: flex;
          background-color: #f5f5f5;
          border-bottom: 1px solid #e0e0e0;
        ">
          <button class="sensor-tab ${currentSensor === "종합" ? "active" : ""}" data-sensor="종합" style="
            flex: 1;
            padding: 10px 5px;
            background: ${currentSensor === "종합" ? "#4a9b94" : "#ccc"};
            border: none;
            cursor: pointer;
            font-size: 13px;
            color: #ffffff;
            font-weight: 500;
          ">종합</button>
          ${location.devices ? location.devices
            .filter(device => device.status === 'ACTIVE')
            .map(device => `
              <button class="sensor-tab ${currentSensor === `센서${device.sensorNumber}` ? "active" : ""}" data-sensor="센서${device.sensorNumber}" style="
                flex: 1;
                padding: 10px 5px;
                background: ${currentSensor === `센서${device.sensorNumber}` ? "#4a9b94" : "#ccc"};
                border: none;
                cursor: pointer;
                font-size: 13px;
                color: #ffffff;
                font-weight: 500;
              ">센서${device.sensorNumber}</button>
            `).join('') : ''}
        </div>
        
        <div class="data-grid" style="padding: 15px; display: flex; gap: 15px;">
          <div style="flex: 1;">
            <div style="display: flex; align-items: center; padding: 4px 0;">
              <span style="width: 80px; font-size: 13px; color: #333; font-weight: 500;">복합악취</span>
              <span style="width: 100px; font-size: 12px; color: #666;">측정값(배수) :</span>
              <span style="width: 60px; font-size: 15px; font-weight: 600; color: #000; text-align: right;">0</span>
              <span style="
                margin-left: 15px;
                font-size: 12px;
                padding: 3px 8px;
                border-radius: 3px;
                text-align: center;
                font-weight: 500;
                color: ${
                  getStatusClass(getCompositeStatus(currentData.h2s, currentData.nh3)) === "good"
                    ? "#4CAF50"
                    : getStatusClass(getCompositeStatus(currentData.h2s, currentData.nh3)) === "normal"
                      ? "#2196F3"
                      : getStatusClass(getCompositeStatus(currentData.h2s, currentData.nh3)) === "bad"
                        ? "#FF9800"
                        : getStatusClass(getCompositeStatus(currentData.h2s, currentData.nh3)) === "no-data"
                          ? "#666"
                          : "#F44336"
                };
                background-color: ${
                  getStatusClass(getCompositeStatus(currentData.h2s, currentData.nh3)) === "good"
                    ? "#E8F5E9"
                    : getStatusClass(getCompositeStatus(currentData.h2s, currentData.nh3)) === "normal"
                      ? "#E3F2FD"
                      : getStatusClass(getCompositeStatus(currentData.h2s, currentData.nh3)) === "bad"
                        ? "#FFF3E0"
                        : getStatusClass(getCompositeStatus(currentData.h2s, currentData.nh3)) === "no-data"
                          ? "#F5F5F5"
                          : "#FFEBEE"
                };
              ">${getCompositeStatus(currentData.h2s, currentData.nh3)}</span>
            </div>
            <div style="display: flex; align-items: center; padding: 4px 0;">
              <span style="width: 80px; font-size: 13px; color: #333; font-weight: 500;">황화수소</span>
              <span style="width: 100px; font-size: 12px; color: #666;">측정값(ppm) :</span>
              <span style="width: 60px; font-size: 15px; font-weight: 600; color: #000; text-align: right;">${formatValue(currentData.h2s)}</span>
              <span style="
                margin-left: 15px;
                font-size: 12px;
                padding: 3px 8px;
                border-radius: 3px;
                text-align: center;
                font-weight: 500;
                color: ${
                  getStatusClass(getStatus(currentData.h2s, "h2s")) === "good"
                    ? "#4CAF50"
                    : getStatusClass(getStatus(currentData.h2s, "h2s")) === "normal"
                      ? "#2196F3"
                      : getStatusClass(getStatus(currentData.h2s, "h2s")) === "bad"
                        ? "#FF9800"
                        : "#F44336"
                };
                background-color: ${
                  getStatusClass(getStatus(currentData.h2s, "h2s")) === "good"
                    ? "#E8F5E9"
                    : getStatusClass(getStatus(currentData.h2s, "h2s")) === "normal"
                      ? "#E3F2FD"
                      : getStatusClass(getStatus(currentData.h2s, "h2s")) === "bad"
                        ? "#FFF3E0"
                        : "#FFEBEE"
                };
              ">${getStatus(currentData.h2s, "h2s")}</span>
            </div>
            <div style="display: flex; align-items: center; padding: 4px 0;">
              <span style="width: 80px; font-size: 13px; color: #333; font-weight: 500;">암모니아</span>
              <span style="width: 100px; font-size: 12px; color: #666;">측정값(ppm) :</span>
              <span style="width: 60px; font-size: 15px; font-weight: 600; color: #000; text-align: right;">${formatValue(currentData.nh3)}</span>
              <span style="
                margin-left: 15px;
                font-size: 12px;
                padding: 3px 8px;
                border-radius: 3px;
                text-align: center;
                font-weight: 500;
                color: ${
                  getStatusClass(getStatus(currentData.nh3, "nh3")) === "good"
                    ? "#4CAF50"
                    : getStatusClass(getStatus(currentData.nh3, "nh3")) === "normal"
                      ? "#2196F3"
                      : getStatusClass(getStatus(currentData.nh3, "nh3")) === "bad"
                        ? "#FF9800"
                        : "#F44336"
                };
                background-color: ${
                  getStatusClass(getStatus(currentData.nh3, "nh3")) === "good"
                    ? "#E8F5E9"
                    : getStatusClass(getStatus(currentData.nh3, "nh3")) === "normal"
                      ? "#E3F2FD"
                      : getStatusClass(getStatus(currentData.nh3, "nh3")) === "bad"
                        ? "#FFF3E0"
                        : "#FFEBEE"
                };
              ">${getStatus(currentData.nh3, "nh3")}</span>
            </div>
            <div style="display: flex; align-items: center; padding: 4px 0; ${currentSensor === "종합" ? "" : ""}">
              <span style="width: 80px; font-size: 13px; color: #333; font-weight: 500;">메탄가스</span>
              <span style="width: 100px; font-size: 12px; color: #666;">측정값(ppm) :</span>
              <span style="width: 60px; font-size: 15px; font-weight: 600; color: #000; text-align: right;">${formatValue(currentData.tvoc)}</span>
              <span style="
                margin-left: 15px;
                font-size: 12px;
                padding: 3px 8px;
                border-radius: 3px;
                text-align: center;
                font-weight: 500;
                color: ${
                  getStatusClass(getStatus(currentData.tvoc, "tvoc")) === "good"
                    ? "#4CAF50"
                    : getStatusClass(getStatus(currentData.tvoc, "tvoc")) === "normal"
                      ? "#2196F3"
                      : getStatusClass(getStatus(currentData.tvoc, "tvoc")) === "bad"
                        ? "#FF9800"
                        : "#F44336"
                };
                background-color: ${
                  getStatusClass(getStatus(currentData.tvoc, "tvoc")) === "good"
                    ? "#E8F5E9"
                    : getStatusClass(getStatus(currentData.tvoc, "tvoc")) === "normal"
                      ? "#E3F2FD"
                      : getStatusClass(getStatus(currentData.tvoc, "tvoc")) === "bad"
                        ? "#FFF3E0"
                        : "#FFEBEE"
                };
              ">${getStatus(currentData.tvoc, "tvoc")}</span>
            </div>
            
            ${
              currentSensor === "종합"
                ? `
              <div style="border-top: 2px solid #e0e0e0; margin-top: 15px; padding-top: 15px;">
                <div style="display: grid; grid-template-columns: 1fr 1fr 1fr 1fr; gap: 5px;">
                  <div style="display: flex; align-items: center; padding: 8px 0;">
                    <span style="width: 40px; font-size: 13px; color: #333; font-weight: 500;">온도</span>
                    <span style="font-size: 14px; font-weight: 600; color: #FF9800;">${location.data.temperature === -1 ? "-" : location.data.temperature.toFixed(2)}℃</span>
                  </div>
                  <div style="display: flex; align-items: center; padding: 8px 0;">
                    <span style="width: 40px; font-size: 13px; color: #333; font-weight: 500;">습도</span>
                    <span style="font-size: 14px; font-weight: 600; color: #2196F3;">${location.data.humidity === -1 ? "-" : location.data.humidity.toFixed(2)}%</span>
                  </div>
                  <div style="display: flex; align-items: center; padding: 8px 0;">
                    <span style="width: 40px; font-size: 13px; color: #333; font-weight: 500;">풍향</span>
                    <span style="font-size: 14px; font-weight: 600; color: #333;">${location.data.windDirection}</span>
                  </div>
                  <div style="display: flex; align-items: center; padding: 8px 0;">
                    <span style="width: 40px; font-size: 13px; color: #333; font-weight: 500;">풍속</span>
                    <span style="font-size: 14px; font-weight: 600; color: #4CAF50;">${location.data.windSpeed === -1 ? "-" : location.data.windSpeed.toFixed(2)}m/s</span>
                  </div>
                </div>
              </div>
            `
                : ""
            }
          </div>
          
          <div style="display: flex; flex-direction: column; justify-content: center; align-items: center; padding: 0px; gap: 20px;">
            <div style="display: flex; flex-direction: column; align-items: center; gap: 10px;">
              <img src="${
                location.type === "good"
                  ? "/img/ds_ico01.png"
                  : location.type === "normal"
                    ? "/img/ds_ico02.png"
                    : location.type === "warning"
                      ? "/img/ds_ico03.png"
                      : "/img/ds_ico04.png"
              }" alt="${
                location.type === "good"
                  ? "좋음"
                  : location.type === "normal"
                    ? "보통"
                    : location.type === "warning"
                      ? "나쁨"
                      : "매우나쁨"
              }" style="width: 60px; height: 60px;" />
              <span style="font-size: 18px; color: #333; font-weight: 700;">${
                location.type === "good"
                  ? "좋음"
                  : location.type === "normal"
                    ? "보통"
                    : location.type === "warning"
                      ? "나쁨"
                      : "매우나쁨"
              }</span>
            </div>
            <button class="graph-button" style="
              background-color: #5AC8FA;
              color: white;
              border: none;
              padding: 8px 8px;
              border-radius: 6px;
              cursor: pointer;
              font-size: 15px;
              font-weight: 600;
              white-space: nowrap;
            ">그래프 보기</button>
          </div>
        </div>
      </div>
    `;
  };

  const attachInfoWindowEvents = (location: MarkerData, currentSensor: string) => {
    // 센서 탭 클릭 이벤트
    const sensorTabs = document.querySelectorAll(".sensor-tab");
    sensorTabs.forEach((tab) => {
      tab.addEventListener("click", (e) => {
        const selectedSensor = (e.target as HTMLElement).getAttribute("data-sensor");
        if (selectedSensor && selectedSensor !== currentSensor) {
          // 선택된 센서로 정보 업데이트
          updateInfoWindowContent(location, selectedSensor);
        }
      });
    });

    // 그래프 버튼 클릭
    const graphBtn = document.querySelector(".graph-button");
    if (graphBtn) {
      graphBtn.addEventListener("click", () => {
        openGraphPopup(location);
      });
    }
  };

  // 정보창 내용 업데이트 함수
  const updateInfoWindowContent = (location: MarkerData, sensor: string) => {
    if (infoWindowRef.current) {
      const newContent = createInfoWindowContent(location, sensor);
      infoWindowRef.current.setContent(newContent);
      
      // DOM 업데이트 후 이벤트 재바인딩
      setTimeout(() => {
        attachInfoWindowEvents(location, sensor);
      }, 100);
    }
  };

  return (
    <div
      style={{
        width: "100%",
        height: "100vh",
        display: "flex",
        flexDirection: "column",
        backgroundColor: "#f0f0f0",
        overflow: "hidden",
      }}
    >
      {/* 상단 헤더 */}
      <div
        style={{
          backgroundColor: "#2C2C2C",
          padding: "10px 20px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "10px",
          }}
        >
          <span
            style={{
              color: "white",
              padding: "6px 5px 6px 10px",
              fontSize: "13px",
            }}
          >
            주소검색
          </span>

          <select
            style={{
              backgroundColor: "#444",
              color: "white",
              border: "1px solid #555",
              padding: "6px 10px",
              borderRadius: "3px",
              fontSize: "13px",
              cursor: "pointer",
            }}
            disabled
          >
            <option>전라남도</option>
          </select>

          <select
            style={{
              backgroundColor: "#444",
              color: "white",
              border: "1px solid #555",
              padding: "6px 10px",
              borderRadius: "3px",
              fontSize: "13px",
              cursor: "pointer",
            }}
            disabled
          >
            <option>나주시</option>
          </select>

          <select
            style={{
              backgroundColor: "#444",
              color: "white",
              border: "1px solid #555",
              padding: "6px 10px",
              borderRadius: "3px",
              fontSize: "13px",
              cursor: "pointer",
            }}
            value={selectedRegion}
            onChange={(e) => handleRegionChange(e.target.value)}
          >
            <option value="">전체</option>
            {regions
              .filter(region => region.code !== "4617000000") // 나주시 전체 제외
              .map((region) => (
                <option key={region.code} value={region.name}>
                  {region.name}
                </option>
              ))}
          </select>

          {/*<span*/}
          {/*    style={{*/}
          {/*      color: "white",*/}
          {/*      padding: "6px 5px 6px 10px",*/}
          {/*      fontSize: "13px",*/}
          {/*    }}*/}
          {/*>*/}
          {/*  검색어*/}
          {/*</span>*/}
          {/*<input*/}
          {/*  type="text"*/}
          {/*  placeholder="사업장명을 입력해 주세요."*/}
          {/*  value={searchKeyword}*/}
          {/*  onChange={(e) => setSearchKeyword(e.target.value)}*/}
          {/*  onKeyPress={handleKeyPress}*/}
          {/*  style={{*/}
          {/*    backgroundColor: "#444",*/}
          {/*    color: "white",*/}
          {/*    border: "1px solid #555",*/}
          {/*    padding: "6px 12px",*/}
          {/*    borderRadius: "3px",*/}
          {/*    width: "180px",*/}
          {/*    fontSize: "13px",*/}
          {/*  }}*/}
          {/*/>*/}

          {/*<button*/}
          {/*  onClick={handleSearch}*/}
          {/*  style={{*/}
          {/*    backgroundColor: "transparent",*/}
          {/*    backgroundImage: "url(/img/ico_sch02.png)",*/}
          {/*    backgroundRepeat: "no-repeat",*/}
          {/*    backgroundPosition: "center",*/}
          {/*    color: "white",*/}
          {/*    border: "none",*/}
          {/*    padding: "6px 12px",*/}
          {/*    borderRadius: "3px",*/}
          {/*    cursor: "pointer",*/}
          {/*    fontSize: "16px",*/}
          {/*    width: "30px",*/}
          {/*    height: "30px",*/}
          {/*  }}*/}
          {/*>*/}
          {/*</button>*/}
        </div>

        <button
          style={{
            backgroundColor: "#5AC8FA",
            color: "white",
            border: "none",
            padding: "8px 20px",
            borderRadius: "3px",
            cursor: "pointer",
            fontSize: "13px",
          }}
        >
          측정소별 숨기기
        </button>
      </div>

      {/* 메인 컨텐츠 */}
      <div
        style={{
          flex: 1,
          display: "flex",
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* 좌측 범례 */}
        <div
          style={{
            position: "absolute",
            left: "20px",
            top: "20px",
            display: "flex",
            gap: "6px",
            backgroundColor: "rgba(0,0,0,0.63)",
            borderRadius: "8px",
            padding: "15px",
            boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
            zIndex: 100,
          }}
        >
          <div
            style={{
              alignItems: "center",
              marginBottom: "12px",
              gap: "10px",
            }}
          >
            <img src="/img/ds_ico01.png" alt="좋음" style={{ width: "35px", height: "35px" }} />
            <span style={{ display: "block", textAlign: "center", fontSize: "14px", color: "#fff", fontWeight: 500 }}>좋음</span>
          </div>
          <div
            style={{
              alignItems: "center",
              marginBottom: "12px",
              gap: "10px",
            }}
          >
            <img src="/img/ds_ico02.png" alt="보통" style={{ width: "35px", height: "35px" }} />
            <span style={{ display: "block", textAlign: "center", fontSize: "14px", color: "#fff", fontWeight: 500 }}>보통</span>
          </div>
          <div
            style={{
              alignItems: "center",
              marginBottom: "12px",
              gap: "10px",
            }}
          >
            <img src="/img/ds_ico03.png" alt="나쁨" style={{ width: "35px", height: "35px" }} />
            <span style={{ display: "block", textAlign: "center", fontSize: "14px", color: "#fff", fontWeight: 500 }}>나쁨</span>
          </div>
          <div
            style={{
              alignItems: "center",
              gap: "10px",
            }}
          >
            <img src="/img/ds_ico04.png" alt="매우나쁨" style={{ width: "35px", height: "35px" }} />
            <span style={{ display: "block", textAlign: "center", fontSize: "14px", color: "#fff", fontWeight: 500 }}>
              매우
              <br />
              나쁨
            </span>
          </div>
        </div>

        {/* 지도 영역 */}
        <div style={{ flex: 1, position: "relative" }}>
          <div ref={mapRef} style={{ width: "100%", height: "100%" }} />
        </div>
      </div>

      {/* 그래프 팝업 */}
      {showGraphPopup && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            zIndex: 1000,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowGraphPopup(false);
            }
          }}
        >
          <div
            style={{
              backgroundColor: "#1a1a1a",
              color: "white",
              borderRadius: "8px",
              padding: "20px",
              width: "800px",
              maxWidth: "90vw",
              maxHeight: "80vh",
              overflow: "auto",
              position: "relative",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "20px",
                borderBottom: "1px solid #333",
                paddingBottom: "15px",
              }}
            >
              <h3 style={{ color: "#ffffff", margin: 0, fontSize: "18px" }}>
                {popupDeviceData?.name} - {new Date().toLocaleDateString('ko-KR', {
                  year: 'numeric',
                  month: '2-digit',
                  day: '2-digit'
                }).replace(/\./g, '.').replace(/\s/g, '')} 시간대별 수치
              </h3>
              <button
                onClick={() => setShowGraphPopup(false)}
                style={{
                  backgroundColor: "transparent",
                  color: "white",
                  border: "none",
                  fontSize: "20px",
                  cursor: "pointer",
                  padding: "5px",
                }}
              >
                ✕
              </button>
            </div>

            <div style={{ marginBottom: "20px" }}>
              {/* 센서 탭 메뉴 - 실제 사업장의 센서 수에 맞게 표시 */}
              <div
                style={{
                  display: "flex",
                  gap: "5px",
                  marginBottom: "15px",
                  borderBottom: "1px solid #333",
                  paddingBottom: "10px",
                }}
              >
                {(() => {
                  // 해당 사업장의 활성화된 센서만 표시
                  const availableSensors = ["종합"];
                  if (popupDeviceData && popupDeviceData.devices) {
                    const activeDevices = popupDeviceData.devices.filter(device => device.status === 'ACTIVE');
                    activeDevices.forEach(device => {
                      if (device.sensorNumber) {
                        availableSensors.push(`센서${device.sensorNumber}`);
                      }
                    });
                  }
                  
                  return availableSensors.map((sensor) => (
                    <button
                      key={sensor}
                      onClick={() => setSelectedSensor(sensor)}
                      style={{
                        backgroundColor: selectedSensor === sensor ? "#1dd3d6" : "transparent",
                        color: selectedSensor === sensor ? "white" : "#ccc",
                        border: selectedSensor === sensor ? "1px solid #1dd3d6" : "1px solid #555",
                        padding: "8px 15px",
                        borderRadius: "4px",
                        cursor: "pointer",
                        fontSize: "14px",
                        fontWeight: selectedSensor === sensor ? "bold" : "normal",
                      }}
                    >
                      {sensor}
                    </button>
                  ));
                })()}
              </div>
              
              {/* 측정항목 버튼 */}
              <div
                style={{
                  display: "flex",
                  gap: "10px",
                  flexWrap: "wrap",
                  marginBottom: "15px",
                }}
              >
                {["복합악취", "황화수소", "암모니아", "메탄가스", "온도", "습도"].map(
                  (metric) => (
                    <button
                      key={metric}
                      onClick={() => setSelectedMetric(metric)}
                      style={{
                        backgroundColor: selectedMetric === metric ? "#1dd3d6" : "#444",
                        color: "white",
                        border: "none",
                        padding: "8px 15px",
                        borderRadius: "4px",
                        cursor: "pointer",
                        fontSize: "13px",
                      }}
                    >
                      {metric}
                    </button>
                  )
                )}
              </div>

              <div style={{ height: "300px", marginBottom: "20px", position: "relative" }}>
                {chartLoading ? (
                  <div style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    backgroundColor: "rgba(26, 26, 26, 0.8)",
                    zIndex: 10
                  }}>
                    <div style={{ color: "white", fontSize: "16px" }}>데이터 로딩 중...</div>
                  </div>
                ) : null}
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart
                    data={chartData}
                    margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                    <XAxis
                      dataKey="time"
                      stroke="#666"
                      tick={{ fill: "#999", fontSize: 12 }}
                    />
                    <YAxis
                      stroke="#666"
                      tick={{ fill: "#999", fontSize: 12 }}
                      domain={[0, "dataMax + 1"]}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    <Line
                      type="monotone"
                      dataKey="value"
                      stroke="#1dd3d6"
                      strokeWidth={2}
                      dot={{ fill: "#1dd3d6", r: 4 }}
                      activeDot={{ r: 6 }}
                      isAnimationActive={false}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MonitoringView;
